import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWindowManager } from '@/lib/context/WindowManagerContext';

// ── Pinned pages (CR #114) ──────────────────────────────────────────────────
// Pin any page from the breadcrumb bar; pins live in a slim chip row under the
// breadcrumbs on every screen (great for phone multitasking — jump between a
// job, its invoice, and the timesheet without re-navigating). localStorage-
// persisted; synced across the app via a custom event.

export interface PinnedPage { path: string; label: string }

const LS_KEY = 'pinned_pages_v1';
const EVT = 'pins-changed';

export const getPins = (): PinnedPage[] => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
};

const savePins = (pins: PinnedPage[]) => {
  localStorage.setItem(LS_KEY, JSON.stringify(pins.slice(0, 12)));
  window.dispatchEvent(new Event(EVT));
};

export const togglePin = (page: PinnedPage) => {
  const pins = getPins();
  const idx = pins.findIndex(p => p.path === page.path);
  if (idx >= 0) pins.splice(idx, 1);
  else pins.unshift(page);
  savePins(pins);
};

export function usePins(): PinnedPage[] {
  const [pins, setPins] = useState<PinnedPage[]>(getPins);
  useEffect(() => {
    const h = () => setPins(getPins());
    window.addEventListener(EVT, h);
    window.addEventListener('storage', h);
    return () => { window.removeEventListener(EVT, h); window.removeEventListener('storage', h); };
  }, []);
  return pins;
}

/** Star toggle for the current page — lives at the end of the breadcrumb bar. */
export function PinButton({ label }: { label: string }) {
  const { pathname } = useLocation();
  const pins = usePins();
  const pinned = pins.some(p => p.path === pathname);
  return (
    <button
      onClick={() => togglePin({ path: pathname, label })}
      title={pinned ? 'Unpin this page' : 'Pin this page for quick access'}
      className={`ml-auto flex items-center justify-center h-6 w-6 rounded flex-shrink-0 ${pinned ? 'text-primary' : 'text-gray-300 hover:text-primary'}`}
    >
      <span className="material-symbols-outlined text-base" style={pinned ? { fontVariationSettings: "'FILL' 1" } : undefined}>
        push_pin
      </span>
    </button>
  );
}

/** Chip row of pinned pages — hidden when nothing is pinned. */
export function PinBar() {
  const pins = usePins();
  const { pathname } = useLocation();
  // #182: pin chips open as floating windows by default
  const { openWindow } = useWindowManager();
  if (pins.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none whitespace-nowrap px-2 md:px-6 py-1 border-b border-gray-100 bg-[#FAF6F0]/60 flex-shrink-0">
      <span className="material-symbols-outlined text-sm text-primary flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>push_pin</span>
      {pins.map(p => (
        <span key={p.path} className={`flex items-center gap-0.5 rounded-full border text-[11px] font-semibold flex-shrink-0 ${
          p.path === pathname ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary/50'
        }`}>
          <button
            onClick={() => openWindow({ route: p.path, title: p.label, icon: 'push_pin' })}
            className="pl-2 py-0.5 max-w-[10rem] truncate"
          >
            {p.label}
          </button>
          <button
            onClick={() => togglePin(p)}
            title="Unpin"
            className={`pr-1 pl-0.5 ${p.path === pathname ? 'text-white/70 hover:text-white' : 'text-gray-300 hover:text-red-500'}`}
          >
            <span className="material-symbols-outlined text-[13px] leading-none">close</span>
          </button>
        </span>
      ))}
    </div>
  );
}
